/**
 * FileWidget component.
 * @module components/manage/Widgets/FileWidget
 */
// Shadow of Volto 19.3.0 components/manage/Widgets/FileWidget.jsx, the file
// field of the forms Neu -> Datei and Neu -> Bild (#22832)

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dimmer } from 'semantic-ui-react';
import { readAsDataURL } from 'promise-file-reader';
import { injectIntl } from 'react-intl';
import deleteSVG from '@plone/volto/icons/delete.svg';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Image from '@plone/volto/components/theme/Image/Image';
import loadable from '@loadable/component';
import { validateFileUploadSize } from '@plone/volto/helpers/FormValidation/FormValidation';
import { defineMessages, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
/* custom code start: limits of the form and of the mass upload */
import {
  FORM_FILE_UPLOAD_SIZE,
  MAX_FILE_UPLOAD_SIZE,
  formatUploadSize,
} from '@horn-und-horn/volto-theme/constants/upload';
/* custom code end */

const imageMimetypes = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/jpg',
  'image/gif',
  'image/svg+xml',
];
const Dropzone = loadable(() => import('react-dropzone'));

const messages = defineMessages({
  releaseDrag: {
    id: 'Drop files here ...',
    defaultMessage: 'Drop files here ...',
  },
  editFile: {
    id: 'Drop file here to replace the existing file',
    defaultMessage:
      'File upload area. Press Enter or click to replace the existing file.',
  },
  fileDrag: {
    id: 'Drop file here to upload a new file',
    defaultMessage:
      'File upload area. Press Enter or click to open the file browser.',
  },
  replaceFile: {
    id: 'Replace existing file',
    defaultMessage: 'Replace existing file',
  },
  addNewFile: {
    id: 'Choose a file',
    defaultMessage: 'Choose a file',
  },
  maxSizeError: {
    id: 'The file you uploaded exceeded the maximum allowed size of {size} bytes',
    defaultMessage:
      'The file you uploaded exceeded the maximum allowed size of {size} bytes',
  },
  acceptError: {
    id: 'File is not of the accepted type {accept}',
    defaultMessage: 'File is not of the accepted type {accept}',
  },
  dragAndDropActionA11y: {
    id: 'Press Enter to browse files from your computer.',
    defaultMessage:
      'File upload area. Press Enter or click to open the file browser.',
  },
  dragAndDropReplaceA11y: {
    id: 'File upload area. Press Enter or click to replace the existing file',
    defaultMessage:
      'File upload area. Press Enter or click to replace the existing file.',
  },
  requiredField: {
    id: 'This field is required.',
    defaultMessage: 'This field is required.',
  },
  downloadFile: {
    id: 'Download {filename}',
    defaultMessage: 'Download {filename}',
  },
  /* custom code start: file too large for the form (#22832) */
  formFileTooLarge: {
    id: 'formFileTooLarge',
    defaultMessage: 'Die Datei ist für dieses Formular zu groß',
  },
  formFileTooLargeHint: {
    id: 'formFileTooLargeHint',
    defaultMessage:
      'Dateien über {limit} bitte über „Neu → Massenupload“ hochladen (bis {max}).',
  },
  /* custom code end */
});

/**
 * FileWidget component class.
 * @function FileWidget
 * @returns {string} Markup of the component.
 *
 * To use it, in schema properties, declare a field like:
 *
 * ```jsx
 * {
 *  title: "File",
 *  widget: 'file',
 * }
 * ```
 * or:
 *
 * ```jsx
 * {
 *  title: "File",
 *  type: 'object',
 * }
 * ```
 *
 */
const FileWidget = (props) => {
  const { id, value, onChange, isDisabled, fieldSet } = props;
  const [fileType, setFileType] = React.useState(false);
  const intl = useIntl();

  const imgAttrs = React.useMemo(() => {
    const data = {};
    if (value?.download) {
      data.item = {
        '@id': value.download.substring(0, value.download.indexOf('/@@images')),
        image: value,
      };
    } else if (value?.data) {
      data.src = `data:${value['content-type']};${value.encoding},${value.data}`;
    }
    return data;
  }, [value]);

  React.useEffect(() => {
    if (value && imageMimetypes.includes(value['content-type'])) {
      setFileType(true);
    }
  }, [value]);

  /**
   * Drop handler
   * @method onDrop
   * @param {array} files File objects
   * @returns {undefined}
   */
  const onDrop = (files, rejectedFiles) => {
    rejectedFiles.forEach((file) => {
      file.errors.forEach((err) => {
        if (err.code === 'file-too-large') {
          toast.error(
            <Toast
              error
              title={intl.formatMessage(messages.maxSizeError, {
                size: props.size,
              })}
            />,
          );
        }

        if (err.code === 'file-invalid-type') {
          toast.error(
            <Toast
              error
              title={intl.formatMessage(messages.acceptError, {
                accept: props.accept,
              })}
            />,
          );
        }
      });
    });
    if (files.length < 1) return;
    const file = files[0];
    /* custom code start: the form reads the file as base64 into the memory
       of the browser, which fails silently above about 383 MB in Chrome;
       larger files go through the mass upload, which sends them in chunks
       (#22832) */
    if (file.size > FORM_FILE_UPLOAD_SIZE) {
      toast.error(
        <Toast
          error
          title={intl.formatMessage(messages.formFileTooLarge)}
          content={intl.formatMessage(messages.formFileTooLargeHint, {
            limit: formatUploadSize(FORM_FILE_UPLOAD_SIZE),
            max: formatUploadSize(MAX_FILE_UPLOAD_SIZE),
          })}
        />,
      );
      return;
    }
    /* custom code end */
    if (!validateFileUploadSize(file, intl.formatMessage)) return;
    readAsDataURL(file).then((data) => {
      const fields = data.match(/^data:(.*);(.*),(.*)$/);
      onChange(id, {
        data: fields[3],
        encoding: fields[2],
        'content-type': fields[1],
        filename: file.name,
      });
    });

    let reader = new FileReader();
    reader.onload = function () {
      const fields = reader.result.match(/^data:(.*);(.*),(.*)$/);
      if (imageMimetypes.includes(fields[1])) {
        setFileType(true);
        let imagePreview = document.getElementById(`field-${id}-image`);
        if (imagePreview) imagePreview.src = reader.result;
      } else {
        setFileType(false);
      }
    };
    reader.readAsDataURL(files[0]);
  };

  const statusTextA11y = [
    value
      ? intl.formatMessage(messages.dragAndDropReplaceA11y)
      : intl.formatMessage(messages.dragAndDropActionA11y),
    props.required && intl.formatMessage(messages.requiredField),
    value?.filename,
  ]
    .filter(Boolean)
    .join('. ');

  const errorTextA11y = props.error?.length ? props.error.join('. ') : null;

  return (
    <FormFieldWrapper {...props}>
      <Dropzone
        onDrop={onDrop}
        {...(props.size ? { maxSize: props.size } : {})}
        {...(props.accept ? { accept: props.accept } : {})}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            className="file-widget-dropzone"
            role="button"
            aria-labelledby={`fieldset-${fieldSet}-field-label-${id}`}
            aria-describedby={`field-${id}-status`}
            {...getRootProps()}
          >
            {isDragActive && <Dimmer active></Dimmer>}
            {fileType ? (
              <Image
                className="image-preview small ui image"
                id={`field-${id}-image`}
                {...imgAttrs}
              />
            ) : (
              <div className="dropzone-placeholder">
                {isDragActive ? (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.releaseDrag)}
                  </p>
                ) : value ? (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.editFile)}
                  </p>
                ) : (
                  <p className="dropzone-text">
                    {intl.formatMessage(messages.fileDrag)}
                  </p>
                )}
              </div>
            )}

            {/* aria-hidden: keyboard access is handled by the parent div (role="button").
                The label is a visual affordance only. The stopPropagation prevents the Dropzone
                from opening the file dialog twice on click. */}
            <label
              className="label-file-widget-input"
              htmlFor={`field-${id}`}
              aria-hidden="true"
              onClick={(e) => e.stopPropagation()}
            >
              {value
                ? intl.formatMessage(messages.replaceFile)
                : intl.formatMessage(messages.addNewFile)}
            </label>
            <span id={`field-${id}-status`} className="visually-hidden">
              {statusTextA11y}
            </span>
            {errorTextA11y && (
              <span role="alert" className="visually-hidden">
                {errorTextA11y}
              </span>
            )}
            <input
              {...getInputProps({
                type: 'file',
                style: { display: 'none' },
              })}
              id={`field-${id}`}
              {...(props.required && { 'aria-required': true })}
              {...(props.error?.length > 0 && { 'aria-invalid': true })}
              name={id}
              disabled={isDisabled}
            />
          </div>
        )}
      </Dropzone>
      {value && (
        <div className="field-file-name">
          <UniversalLink
            href={value.download}
            aria-label={intl.formatMessage(messages.downloadFile, {
              filename: value.filename,
            })}
            download={true}
          >
            {value.filename}
          </UniversalLink>
          <Button
            type="button"
            icon
            basic
            className="delete-button"
            aria-label="delete file"
            disabled={isDisabled}
            onClick={() => {
              onChange(id, null);
              setFileType(false);
            }}
          >
            <Icon name={deleteSVG} size="20px" />
          </Button>
        </div>
      )}
    </FormFieldWrapper>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
FileWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  fieldSet: PropTypes.string,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.shape({
    '@type': PropTypes.string,
    title: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  wrapped: PropTypes.bool,
};

/**
 * Default properties.
 * @property {Object} defaultProps Default properties.
 * @static
 */
FileWidget.defaultProps = {
  description: null,
  required: false,
  fieldSet: null,
  error: [],
  value: null,
};

export default injectIntl(FileWidget);
